/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `three_d_sensor_calibration` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class ThreeDSensorCalibrationMesg : Mesg {
    public constructor() : super("ThreeDSensorCalibration", Profile.MesgNum.THREE_D_SENSOR_CALIBRATION)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. Whole second part of the timestamp */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** sensor_type. Indicates which sensor the calibration is for */
    public var sensorType: SensorType?
        get() = (getFieldValue(SENSOR_TYPE_FIELD_NUM) as? UByte)?.let { SensorType.fromValue(it) }
        set(value) = setFieldValue(SENSOR_TYPE_FIELD_NUM, value?.value)

    /** calibration_factor. Calibration factor used to convert from raw ADC value to degrees, g, etc. */
    public var calibrationFactor: UInt?
        get() = getFieldValue(CALIBRATION_FACTOR_FIELD_NUM) as? UInt
        set(value) = setFieldValue(CALIBRATION_FACTOR_FIELD_NUM, value)

    /**
     * accel_cal_factor: what calibration_factor means when this message selects it.
     *
     * Accelerometer calibration factor
     */
    public val accelCalFactor: UInt?
        get() = getFieldValue(CALIBRATION_FACTOR_FIELD_NUM, 0, SubFieldSelector.Named("AccelCalFactor")) as? UInt

    public fun setAccelCalFactor(value: UInt?) {
        setFieldValue(CALIBRATION_FACTOR_FIELD_NUM, value, 0, SubFieldSelector.Named("AccelCalFactor"))
    }

    /**
     * gyro_cal_factor: what calibration_factor means when this message selects it.
     *
     * Gyro calibration factor
     */
    public val gyroCalFactor: UInt?
        get() = getFieldValue(CALIBRATION_FACTOR_FIELD_NUM, 0, SubFieldSelector.Named("GyroCalFactor")) as? UInt

    public fun setGyroCalFactor(value: UInt?) {
        setFieldValue(CALIBRATION_FACTOR_FIELD_NUM, value, 0, SubFieldSelector.Named("GyroCalFactor"))
    }

    /** calibration_divisor, in counts. Calibration factor divisor */
    public var calibrationDivisor: UInt?
        get() = getFieldValue(CALIBRATION_DIVISOR_FIELD_NUM) as? UInt
        set(value) = setFieldValue(CALIBRATION_DIVISOR_FIELD_NUM, value)

    /** level_shift. Level shift value used to shift the ADC value back into range */
    public var levelShift: UInt?
        get() = getFieldValue(LEVEL_SHIFT_FIELD_NUM) as? UInt
        set(value) = setFieldValue(LEVEL_SHIFT_FIELD_NUM, value)

    /** Number of values stored in offset_cal. */
    public val numOffsetCal: Int
        get() = getField(OFFSET_CAL_FIELD_NUM)?.numValues ?: 0

    /** offset_cal. Internal calibration factors, one for each: xy, yx, zx */
    public var offsetCal: List<Int?>?
        get() {
            val field = getField(OFFSET_CAL_FIELD_NUM) ?: return null
            return List(field.numValues) { getOffsetCal(it) }
        }
        set(value) {
            removeField(OFFSET_CAL_FIELD_NUM)
            value?.forEachIndexed { index, element -> setOffsetCal(index, element) }
        }

    public fun getOffsetCal(index: Int): Int? =
        getFieldValue(OFFSET_CAL_FIELD_NUM, index) as? Int

    public fun setOffsetCal(index: Int, value: Int?) {
        setFieldValue(OFFSET_CAL_FIELD_NUM, value, index)
    }

    /** Number of values stored in orientation_matrix. */
    public val numOrientationMatrix: Int
        get() = getField(ORIENTATION_MATRIX_FIELD_NUM)?.numValues ?: 0

    /** orientation_matrix. 3 x 3 rotation matrix (row major) */
    public var orientationMatrix: List<Double?>?
        get() {
            val field = getField(ORIENTATION_MATRIX_FIELD_NUM) ?: return null
            return List(field.numValues) { getOrientationMatrix(it) }
        }
        set(value) {
            removeField(ORIENTATION_MATRIX_FIELD_NUM)
            value?.forEachIndexed { index, element -> setOrientationMatrix(index, element) }
        }

    public fun getOrientationMatrix(index: Int): Double? =
        getFieldValue(ORIENTATION_MATRIX_FIELD_NUM, index) as? Double

    public fun setOrientationMatrix(index: Int, value: Double?) {
        setFieldValue(ORIENTATION_MATRIX_FIELD_NUM, value, index)
    }

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val SENSOR_TYPE_FIELD_NUM: UByte = 0u
        public const val CALIBRATION_FACTOR_FIELD_NUM: UByte = 1u
        public const val CALIBRATION_DIVISOR_FIELD_NUM: UByte = 2u
        public const val LEVEL_SHIFT_FIELD_NUM: UByte = 3u
        public const val OFFSET_CAL_FIELD_NUM: UByte = 4u
        public const val ORIENTATION_MATRIX_FIELD_NUM: UByte = 5u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("SensorType", 0u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[1] = Field("CalibrationFactor", 1u, BaseType.UINT32, 1.0, 0.0, "", false)
                .addSubField(
                    SubField("AccelCalFactor", BaseType.UINT32, 1.0, 0.0, "g").also { subField ->
                        subField.addMap(0u, 0)
                    },
                )
                .addSubField(
                    SubField("GyroCalFactor", BaseType.UINT32, 1.0, 0.0, "deg/s").also { subField ->
                        subField.addMap(0u, 1)
                    },
                )
            fields[2] = Field("CalibrationDivisor", 2u, BaseType.UINT32, 1.0, 0.0, "counts", false)
            fields[3] = Field("LevelShift", 3u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[4] = Field("OffsetCal", 4u, BaseType.SINT32, 1.0, 0.0, "", false)
            fields[5] = Field("OrientationMatrix", 5u, BaseType.SINT32, 65535.0, 0.0, "", false)
            return fields
        }
    }
}
